/**
 * SuggestedTasksWidget - Phase 5
 * 
 * Displays AI-generated task suggestions based on:
 * - Budget status
 * - Savings goals
 * - Time-based triggers
 * 
 * @module SuggestedTasksWidget
 */

import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { 
  generateAllSuggestions, 
  suggestionToTask,
  SUGGESTION_PRIORITY 
} from '@/services/suggestionsService';
import { useCategoryEnvelope } from '@/context/CategoryEnvelopeContext';
import { useTheme } from '@/contexts/ThemeContext';

// ============================================================================
// STYLES
// ============================================================================

const getStyles = (isDark) => {
  const textPrimary = isDark ? '#e2e8f0' : '#1f2937';
  const textSecondary = isDark ? '#cbd5e1' : '#6b7280';
  const cardBg = isDark ? 'rgba(30, 41, 59, 0.9)' : 'white';
  const cardBorder = isDark ? '#334155' : '#e5e7eb';
  const buttonBg = isDark ? '#1f2937' : '#f3f4f6';
  const hoverBg = isDark ? '#334155' : '#e5e7eb';
  const toggleColor = isDark ? '#94a3b8' : '#6b7280';

  return {
    container: {
      marginBottom: '24px',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '12px',
    },
    title: {
      fontSize: '14px',
      fontWeight: '600',
      color: textPrimary,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    badge: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      fontSize: '11px',
      fontWeight: '600',
      padding: '2px 8px',
      borderRadius: '10px',
    },
    toggleButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '13px',
      color: toggleColor,
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '4px 8px',
      borderRadius: '6px',
      transition: 'background 0.2s',
    },
    suggestionsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    suggestionCard: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 16px',
      background: cardBg,
      borderRadius: '12px',
      border: `1px solid ${cardBorder}`,
      transition: 'all 0.2s',
    },
    suggestionIcon: {
      fontSize: '24px',
      flexShrink: 0,
    },
    suggestionContent: {
      flex: 1,
      minWidth: 0,
    },
    suggestionTitle: {
      fontSize: '14px',
      fontWeight: '500',
      color: textPrimary,
      marginBottom: '2px',
    },
    suggestionDesc: {
      fontSize: '12px',
      color: textSecondary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    priorityBadge: {
      fontSize: '10px',
      fontWeight: '600',
      padding: '2px 6px',
      borderRadius: '4px',
      textTransform: 'uppercase',
    },
    actionButtons: {
      display: 'flex',
      gap: '6px',
      flexShrink: 0,
    },
    addButton: {
      width: '32px',
      height: '32px',
      borderRadius: '8px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      border: 'none',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'transform 0.2s, opacity 0.2s',
    },
    dismissButton: {
      width: '32px',
      height: '32px',
      borderRadius: '8px',
      background: buttonBg,
      border: 'none',
      color: textSecondary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'background 0.2s',
    },
    hoverBg,
    emptyState: {
      padding: '24px',
      textAlign: 'center',
      color: textSecondary,
      fontSize: '13px',
      background: isDark ? '#0f172a' : '#f9fafb',
      borderRadius: '12px',
      border: `1px solid ${cardBorder}`,
    },
    emptyIcon: {
      fontSize: '32px',
      marginBottom: '8px',
      opacity: 0.5,
    },
  };
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const PriorityBadge = ({ priority, styles }) => {
  const colors = {
    [SUGGESTION_PRIORITY.URGENT]: { bg: '#fef2f2', text: '#dc2626' },
    [SUGGESTION_PRIORITY.HIGH]: { bg: '#fff7ed', text: '#ea580c' },
    [SUGGESTION_PRIORITY.MEDIUM]: { bg: '#fefce8', text: '#ca8a04' },
    [SUGGESTION_PRIORITY.LOW]: { bg: '#f0fdf4', text: '#16a34a' },
  };

  const color = colors[priority] || colors[SUGGESTION_PRIORITY.MEDIUM];

  return (
    <span style={{
      ...styles.priorityBadge,
      background: color.bg,
      color: color.text
    }}>
      {priority}
    </span>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const SuggestedTasksWidget = ({
  onAddSuggestion,
  listId,
  goals = [],
  currencySymbol = '£',
  maxDisplay = 3,
}) => {
  const { isDark } = useTheme();
  const styles = useMemo(() => getStyles(isDark), [isDark]);
  const [dismissed, setDismissed] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [addingId, setAddingId] = useState(null);

  // Get envelopes from context
  const { envelopes } = useCategoryEnvelope();

  // Generate suggestions
  const allSuggestions = useMemo(() => {
    return generateAllSuggestions({
      envelopes: envelopes || [],
      goals: goals || [],
      currencySymbol,
    });
  }, [envelopes, goals, currencySymbol]);

  // Filter out dismissed suggestions
  const activeSuggestions = useMemo(() => {
    return allSuggestions.filter(s => !dismissed.includes(s.id));
  }, [allSuggestions, dismissed]);

  // Determine how many to show
  const displayedSuggestions = isExpanded 
    ? activeSuggestions 
    : activeSuggestions.slice(0, maxDisplay);

  const hasMore = activeSuggestions.length > maxDisplay;

  const handleAddSuggestion = async (suggestion) => {
    if (!listId || addingId) return;

    setAddingId(suggestion.id);
    try {
      const task = suggestionToTask(suggestion, listId);
      if (task) {
        await onAddSuggestion(task);
        // Dismiss after adding
        setDismissed(prev => [...prev, suggestion.id]);
      }
    } catch (error) {
      console.error('[SuggestedTasksWidget] Error adding suggestion:', error);
    } finally {
      setAddingId(null);
    }
  };

  const handleDismiss = (suggestionId) => {
    setDismissed(prev => [...prev, suggestionId]);
  };

  // Don't render anything if no suggestions
  if (activeSuggestions.length === 0) {
    return null;
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.title}>
          <Sparkles size={16} style={{ color: '#667eea' }} />
          Smart Suggestions
          <span style={styles.badge}>{activeSuggestions.length}</span>
        </div>
        {hasMore && (
          <button
            style={styles.toggleButton}
            onClick={() => setIsExpanded(!isExpanded)}
            onMouseOver={(e) => e.target.style.background = styles.hoverBg}
            onMouseOut={(e) => e.target.style.background = 'transparent'}
          >
            {isExpanded ? 'Show less' : `+${activeSuggestions.length - maxDisplay} more`}
            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        )}
      </div>

      {/* Suggestions List */}
      <div style={styles.suggestionsList}>
        <AnimatePresence>
          {displayedSuggestions.map((suggestion, index) => {
            const isAdding = addingId === suggestion.id;

            return (
              <motion.div
                key={suggestion.id}
                style={styles.suggestionCard}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ 
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                  borderColor: '#d1d5db',
                }}
              >
                <span style={styles.suggestionIcon}>{suggestion.icon}</span>
                
                <div style={styles.suggestionContent}>
                  <div style={styles.suggestionTitle}>{suggestion.title}</div>
                  <div style={styles.suggestionDesc}>{suggestion.description}</div>
                </div>

                <PriorityBadge priority={suggestion.priority} styles={styles} />

                <div style={styles.actionButtons}>
                  {suggestion.taskTemplate && (
                    <motion.button
                      style={{
                        ...styles.addButton,
                        opacity: isAdding ? 0.7 : 1,
                      }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleAddSuggestion(suggestion)}
                      disabled={isAdding}
                      title="Add as task"
                    >
                      {isAdding ? (
                        <motion.span
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          style={{ fontSize: '12px' }}
                        >
                          ⏳
                        </motion.span>
                      ) : (
                        <Plus size={16} />
                      )}
                    </motion.button>
                  )}
                  <button
                  style={styles.dismissButton}
                  onClick={() => handleDismiss(suggestion.id)}
                  onMouseOver={(e) => e.target.style.background = styles.hoverBg}
                  onMouseOut={(e) => e.target.style.background = styles.dismissButton.background}
                  title="Dismiss"
                >
                  <X size={14} />
                </button>
              </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SuggestedTasksWidget;
